import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  useTheme,
  alpha,
  Paper,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
  Alert,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
  Language as LanguageIcon,
  LocalHospital as HospitalIcon,
  Store as StoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalendarToday as CalendarTodayIcon,
  Close,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Help as HelpIcon,
  Translate as TranslateIcon,
  VideoCall as VideoCallIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useSelector } from 'react-redux';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(8px)',
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.2s ease-in-out',
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.4),
    },
  },
}));

const consultationTypes = [
  'Video Consultation',
  'In-Person',
  'Chat Consultation',
  'Home Visit',
];

const languages = [
  'English',
  'Hindi',
  'Tamil',
  'Telugu',
  'Kannada',
  'Malayalam',
  'Bengali',
  'Marathi',
  'Gujarati',
  'Urdu',
];

const daysOfWeek = [
  { value: 'Mon', label: 'Monday', color: '#FF6B6B' },
  { value: 'Tue', label: 'Tuesday', color: '#4ECDC4' },
  { value: 'Wed', label: 'Wednesday', color: '#45B7D1' },
  { value: 'Thu', label: 'Thursday', color: '#96CEB4' },
  { value: 'Fri', label: 'Friday', color: '#FFEEAD' },
  { value: 'Sat', label: 'Saturday', color: '#D4A5A5' },
  { value: 'Sun', label: 'Sunday', color: '#9B59B6' },
];

const PracticeSetup = ({ formData, onChange, errors }) => {
  const theme = useTheme();
  const { availableOptions } = useSelector((state) => state.onboarding);
  const { practiceSetup } = formData;
  const [isClinicOwner, setIsClinicOwner] = useState(practiceSetup.isClinicOwner || false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [availabilityDialog, setAvailabilityDialog] = useState(false);
  const [availabilityData, setAvailabilityData] = useState({
    day: '',
    start_time: '',
    end_time: '',
    slot_duration: 30,
  });
  const [localErrors, setLocalErrors] = useState({});

  const handleChange = (field) => (event) => {
    onChange('practiceSetup', { [field]: event.target.value });
  };

  const handleMultiSelectChange = (field) => (event) => {
    onChange('practiceSetup', { [field]: event.target.value });
  };

  const handleClinicOwnerChange = (event) => {
    setIsClinicOwner(event.target.checked);
    onChange('practiceSetup', { isClinicOwner: event.target.checked });
  };

  const handleAvailabilityAdd = (day) => {
    setAvailabilityData({ ...availabilityData, day });
    setAvailabilityDialog(true);
  };

  const handleAvailabilitySubmit = () => {
    const newAvailabilities = [...(practiceSetup.availabilities || [])];
    newAvailabilities.push(availabilityData);
    onChange('practiceSetup', { availabilities: newAvailabilities });
    setAvailabilityDialog(false);
    setAvailabilityData({
      day: '',
      start_time: '',
      end_time: '',
      slot_duration: 30,
    });
  };

  const handleAvailabilityDelete = (index) => {
    const newAvailabilities = [...(practiceSetup.availabilities || [])];
    newAvailabilities.splice(index, 1);
    onChange('practiceSetup', { availabilities: newAvailabilities });
  };

  const getDayAvailability = (day) => {
    return (practiceSetup.availabilities || []).filter(avail => avail.day === day);
  };

  const handleTimeChange = (field, time) => {
    onChange('practiceSetup', { [field]: time });
  };

  const handleAvailabilityChange = (day, field, value) => {
    const availabilities = [...(practiceSetup.availabilities || [])];
    const dayIndex = availabilities.findIndex(a => a.day === day);

    if (dayIndex === -1) {
      availabilities.push({
        day,
        isAvailable: field === 'isAvailable' ? value : false,
        startTime: null,
        endTime: null,
      });
    } else {
      availabilities[dayIndex] = {
        ...availabilities[dayIndex],
        [field]: value,
      };
    }

    onChange('practiceSetup', { availabilities });
  };

  const validateFees = (value) => {
    const fee = parseFloat(value);
    if (isNaN(fee) || fee < 0) {
      return 'Please enter a valid amount';
    }
    return null;
  };

  const handleFeeChange = (field) => (event) => {
    const value = event.target.value;
    const error = validateFees(value);
    setLocalErrors(prev => ({ ...prev, [field]: error }));
    if (!error) {
      onChange('practiceSetup', { [field]: value });
    }
  };

  const weekDays = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  const renderClinicInformation = () => (
    <StyledPaper>
      <Typography variant="subtitle1" gutterBottom color="primary">
        <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Clinic Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={isClinicOwner}
                onChange={handleClinicOwnerChange}
                color="primary"
              />
            }
            label="I own or work at a clinic"
          />
        </Grid>
        {isClinicOwner && (
          <>
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Clinic Name"
                value={practiceSetup.clinicName || ''}
                onChange={handleChange('clinicName')}
                error={!!errors.clinicName}
                helperText={errors.clinicName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HospitalIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Clinic Address"
                multiline
                rows={3}
                value={practiceSetup.clinicAddress || ''}
                onChange={handleChange('clinicAddress')}
                error={!!errors.clinicAddress}
                helperText={errors.clinicAddress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Clinic Phone"
                value={practiceSetup.clinicPhone || ''}
                onChange={handleChange('clinicPhone')}
                error={!!errors.clinicPhone}
                helperText={errors.clinicPhone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Clinic Email"
                type="email"
                value={practiceSetup.clinicEmail || ''}
                onChange={handleChange('clinicEmail')}
                error={!!errors.clinicEmail}
                helperText={errors.clinicEmail}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </StyledPaper>
  );

  const renderConsultationSettings = () => (
    <StyledPaper>
      <Typography variant="subtitle1" gutterBottom color="primary">
        <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Consultation Settings
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <StyledTextField
            fullWidth
            label="Consultation Fee (₹)"
            type="number"
            value={practiceSetup.consultationFee || ''}
            onChange={handleFeeChange('consultationFee')}
            error={!!errors.consultationFee || !!localErrors.consultationFee}
            helperText={errors.consultationFee || localErrors.consultationFee}
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <StyledTextField
            fullWidth
            label="Follow-up Fee (₹)"
            type="number"
            value={practiceSetup.followUpFee || ''}
            onChange={handleFeeChange('followUpFee')}
            error={!!errors.followUpFee || !!localErrors.followUpFee}
            helperText={errors.followUpFee || localErrors.followUpFee}
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={consultationTypes}
            value={practiceSetup.consultationTypes || []}
            onChange={handleMultiSelectChange('consultationTypes')}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                label="Consultation Types"
                error={!!errors.consultationTypes}
                helperText={errors.consultationTypes}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option}
                  {...getTagProps({ index })}
                  sx={{
                    background: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                />
              ))
            }
          />
        </Grid>
      </Grid>
    </StyledPaper>
  );

  const renderAvailabilitySettings = () => (
    <StyledPaper>
      <Typography variant="subtitle1" gutterBottom color="primary">
        <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Availability Settings
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Set at least one working day and time slot to get started. You can add more availability later in your dashboard.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              You can manage your complete schedule, including:
            </Typography>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Adding more working days and time slots</li>
              <li>Setting up recurring schedules</li>
              <li>Managing breaks and holidays</li>
              <li>Updating availability in real-time</li>
            </ul>
          </Alert>
          <Grid container spacing={2}>
            {weekDays.map((day) => {
              const dayAvailability = practiceSetup.availabilities?.find(
                (a) => a.day === day
              ) || { isAvailable: false, startTime: null, endTime: null };

              return (
                <Grid item xs={12} sm={6} md={4} key={day}>
                  <Paper
                    sx={{
                      p: 2,
                      border: selectedDay === day ? `2px solid ${daysOfWeek.find(d => d.value === day)?.color}` : undefined,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: theme.shadows[2],
                      },
                    }}
                    onClick={() => setSelectedDay(day)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ color: daysOfWeek.find(d => d.value === day)?.color }}>
                        {day}
                      </Typography>
                      <Button
                        startIcon={<AddIcon />}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAvailabilityAdd(day);
                        }}
                      >
                        Add Slot
                      </Button>
                    </Box>
                    {dayAvailability.isAvailable && (
                      <Stack spacing={1}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          }}
                        >
                          <Typography variant="body2">
                            {dayAvailability.startTime ? dayAvailability.startTime : 'Not set'} - {dayAvailability.endTime ? dayAvailability.endTime : 'Not set'}
                          </Typography>
                          <Box>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAvailabilityChange(day, 'startTime', dayAvailability.startTime);
                                  handleAvailabilityChange(day, 'endTime', dayAvailability.endTime);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAvailabilityChange(day, 'isAvailable', false);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Stack>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
          {errors.availabilities && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {errors.availabilities}
            </Typography>
          )}
        </Grid>
      </Grid>
    </StyledPaper>
  );

  const renderAdditionalInformation = () => (
    <StyledPaper>
      <Typography variant="subtitle1" gutterBottom color="primary">
        <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Additional Information
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <StyledTextField
            fullWidth
            multiline
            rows={4}
            label="Professional Summary"
            value={practiceSetup.professionalSummary || ''}
            onChange={handleChange('professionalSummary')}
            error={!!errors.professionalSummary}
            helperText={errors.professionalSummary || 'Brief description of your practice and expertise'}
          />
        </Grid>
        <Grid item xs={12}>
          <StyledTextField
            fullWidth
            label="Website (Optional)"
            value={practiceSetup.website || ''}
            onChange={handleChange('website')}
            error={!!errors.website}
            helperText={errors.website}
          />
        </Grid>
      </Grid>
    </StyledPaper>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
        Practice Setup
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Let's set up your practice details and availability to help patients find and connect with you.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          {renderClinicInformation()}
        </Grid>
        <Grid item xs={12}>
          {renderConsultationSettings()}
        </Grid>
        <Grid item xs={12}>
          {renderAvailabilitySettings()}
        </Grid>
        <Grid item xs={12}>
          {renderAdditionalInformation()}
        </Grid>
      </Grid>

      {/* Availability Dialog */}
      <Dialog
        open={availabilityDialog}
        onClose={() => setAvailabilityDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {availabilityData.start_time ? 'Edit Time Slot' : 'Add Time Slot'}
            <IconButton onClick={() => setAvailabilityDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              type="time"
              label="Start Time"
              value={availabilityData.start_time}
              onChange={(e) => setAvailabilityData({ ...availabilityData, start_time: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="time"
              label="End Time"
              value={availabilityData.end_time}
              onChange={(e) => setAvailabilityData({ ...availabilityData, end_time: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="number"
              label="Slot Duration (minutes)"
              value={availabilityData.slot_duration}
              onChange={(e) => setAvailabilityData({ ...availabilityData, slot_duration: e.target.value })}
              fullWidth
              required
              inputProps={{ min: 15, max: 120, step: 15 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvailabilityDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAvailabilitySubmit}
            disabled={!availabilityData.start_time || !availabilityData.end_time}
          >
            {availabilityData.start_time ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Fade in timeout={500}>
        <Box sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Your practice setup information helps us:
            </Typography>
            <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Connect you with suitable patients</li>
              <li>Set appropriate expectations for consultations</li>
              <li>Manage your availability effectively</li>
              <li>Present your practice professionally</li>
            </ul>
          </Alert>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Need help setting up your practice?
            </Typography>
            <Tooltip title="Contact our support team for assistance">
              <IconButton size="small" color="primary">
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Fade>

      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ mt: 3, fontStyle: 'italic' }}
      >
        * Required fields are marked with an asterisk. Your practice information will be visible to patients.
      </Typography>
    </Box>
  );
};

export default PracticeSetup; 